<script lang="ts">
    import { onDestroy } from 'svelte';

    export let name: string;
    export let errors: Record<string, string[]>;
    export let label: string = '';
    export let value: string = '';
    export let placeholder: string = '••••••••';
    export let disabled: boolean = false;
    export let required: boolean = false;
    export let showStrength: boolean = false;
    export let matchValue: string | undefined = undefined;

    let visible = false;

    errors[name] = [];

    const validate = (val: string = value) => {
        errors[name] = [];
        if (required && !val) errors[name].push('This field is required');
        if (matchValue !== undefined && val !== matchValue) errors[name].push('Passwords do not match');
    };

    const handleInput = (e: Event) => {
        value = (e.currentTarget as HTMLInputElement).value;
        if (errors[name].length) validate(value);
    };

    const handleBlur = (e: Event) => {
        value = (e.currentTarget as HTMLInputElement).value;
        validate(value);
    };

    const toggleVisible = () => { visible = !visible; };

    type Strength = 'weak' | 'medium' | 'strong';

    const getStrength = (val: string): Strength => {
        if (val.length < 8) return 'weak';
        const mixed = /[a-z]/.test(val) && /[A-Z]/.test(val) && /[0-9]/.test(val);
        const special = /[^a-zA-Z0-9]/.test(val);
        if (val.length >= 12 && mixed && special) return 'strong';
        if (mixed) return 'medium';
        return 'weak';
    };

    $: strength = getStrength(value);

    onDestroy(() => { errors[name] = []; });
</script>

<div class="form-field">
    {#if label}<span class="label-text">{label}</span>{/if}
    <div class="pw-wrap">
        <input
            type={visible ? 'text' : 'password'}
            {name}
            {disabled}
            {required}
            {placeholder}
            value={value}
            on:input={handleInput}
            on:blur={handleBlur}
            class:error={errors[name].length > 0}
            on:change
        />
        <button type="button" class="pw-toggle" on:click={toggleVisible} {disabled} aria-label={visible ? 'Hide password' : 'Show password'} tabindex="-1">
            {#if visible}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            {:else}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            {/if}
        </button>
    </div>
    {#if showStrength && value}
        <div class="strength-bar">
            <div class="strength-fill strength-fill--{strength}"></div>
        </div>
        <span class="strength-label strength-label--{strength}">{strength}</span>
    {/if}
    <span class="field-errors">
        {#each errors[name] as err}<span>{err}</span>{/each}
    </span>
</div>

<style lang="scss">
    .pw-wrap {
        position: relative;
        display: flex;

        input {
            flex: 1;
            margin: 0;
            padding-right: 2.8em;
        }
    }

    .pw-toggle {
        position: absolute;
        right: 0.4em;
        top: 50%;
        transform: translateY(-50%);
        width: 2.4em;
        height: 2.4em;
        background: none;
        border: none;
        color: $color-text-muted;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0;
        border-radius: 50%;
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
            background-color: rgba(128, 128, 128, 0.1);
            color: $color-text;
        }
    }

    .strength-bar {
        height: 4px;
        background-color: $color-bg-active;
        border-radius: 2px;
        overflow: hidden;
        margin-top: 0.4em;
    }

    .strength-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease;

        &--weak   { width: 33%; background-color: $color-danger; }
        &--medium { width: 66%; background-color: $color-warning; }
        &--strong { width: 100%; background-color: $color-success; }
    }

    .strength-label {
        font-size: 0.72em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-top: 0.2em;
        display: block;

        &--weak   { color: $color-danger; }
        &--medium { color: $color-warning; }
        &--strong { color: $color-success; }
    }
</style>
